import * as React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Badge,
    Box,
    Button,
    Alert,
    Chip,
    Divider,
    IconButton,
    List,
    ListItemButton,
    ListItemText,
    Popover,
    Tooltip,
    Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import {useNavigate} from 'react-router-dom';
import {useMarkNotificationsRead, useNotifications} from '../../data/notifications';
import {useRespondToInvitation} from '../../data/invitations';
import {apiErrorMessage} from '../../utilities/apiError';
import {Invitation, Notification} from '../../contract';
import {accentEdge} from '../styles/surfaces';

dayjs.extend(relativeTime);

// The bell in the app bar: a badge with how many notifications are unread, and a panel with the latest ones. Clicking one
// marks it read and, if it is about a board, goes there.
export function NotificationBell() {
    const navigate = useNavigate();
    const {data, isError} = useNotifications();
    const {mutate: markRead} = useMarkNotificationsRead();
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const {mutate: respond, isPending: isResponding, variables: answering, error: respondError, reset: clearRespondError} = useRespondToInvitation();

    const notifications = data?.notifications ?? [];
    const invitations = data?.invitations ?? [];
    // What wants attention: invitations waiting for an answer, and notifications not yet read.
    const unreadCount = (data?.unreadCount ?? 0) + invitations.length;

    const answer = (invitation: Invitation, accept: boolean) =>
        respond({invitationId: invitation.id, accept}, {
            onSuccess: () => {
                if (accept) {
                    setAnchorEl(null);
                    navigate(`/boards/${invitation.boardId}`);
                }
            },
        });

    const openNotification = (notification: Notification) => {
        if (!notification.readAt) markRead([notification.id]);
        setAnchorEl(null);
        if (notification.boardId) navigate(`/boards/${notification.boardId}`);
    };

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    size="large"
                    color="inherit"
                    aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
                    aria-haspopup="true"
                    aria-expanded={Boolean(anchorEl)}
                    onClick={(event) => { clearRespondError(); setAnchorEl(event.currentTarget); }}
                >
                    <Badge badgeContent={unreadCount} color="error" max={99}>
                        {unreadCount > 0 ? <NotificationsIcon/> : <NotificationsNoneIcon/>}
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                slotProps={{paper: {sx: {width: 360, maxWidth: 'calc(100vw - 32px)'}}}}
            >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1}}>
                    <Typography variant="subtitle1" component="h2" fontWeight={600}>Notifications</Typography>
                    <Button size="small" disabled={(data?.unreadCount ?? 0) === 0} onClick={() => markRead(undefined)}>
                        Mark all as read
                    </Button>
                </Box>
                <Divider/>

                {invitations.length > 0 && (
                    <Box>
                        <Typography variant="overline" color="text.secondary" sx={{px: 2, pt: 1, display: 'block'}}>Invitations</Typography>
                        {invitations.map((invitation) => (
                            <Box key={invitation.id} sx={{px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', ...accentEdge('warning.main')}}>
                                <Typography variant="body2" fontWeight={600}>
                                    {invitation.invitedBy} invited you to join “{invitation.boardTitle}”
                                </Typography>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 0.5}}>
                                    <Chip size="small" variant="outlined" label={`as ${invitation.role}`}/>
                                    {invitation.kiosk && <Chip size="small" variant="outlined" color="warning" label="kiosk"/>}
                                    <Typography variant="caption" color="text.secondary">{dayjs(invitation.createdAt).fromNow()}</Typography>
                                </Box>
                                {invitation.kiosk && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 0.5}}>
                                        Accepting limits your account to this board's kiosk display.
                                    </Typography>
                                )}
                                <Box sx={{display: 'flex', gap: 1, mt: 1}}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        disabled={isResponding && answering?.invitationId === invitation.id}
                                        onClick={() => answer(invitation, true)}
                                    >
                                        Accept
                                    </Button>
                                    <Button
                                        size="small"
                                        disabled={isResponding && answering?.invitationId === invitation.id}
                                        onClick={() => answer(invitation, false)}
                                    >
                                        Decline
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                        {respondError && <Alert severity="warning" sx={{m: 1}}>{apiErrorMessage(respondError)}</Alert>}
                    </Box>
                )}

                {isError && !data ? (
                    <Typography color="text.secondary" sx={{p: 2}}>Couldn't load notifications.</Typography>
                ) : notifications.length === 0 && invitations.length === 0 ? (
                    <Typography color="text.secondary" sx={{p: 2}}>You're all caught up.</Typography>
                ) : notifications.length === 0 ? null : (
                    <List disablePadding sx={{maxHeight: 420, overflowY: 'auto'}}>
                        {notifications.map((notification) => (
                            <ListItemButton
                                key={notification.id}
                                divider
                                onClick={() => openNotification(notification)}
                                sx={notification.readAt ? undefined : {...accentEdge('primary.main'), bgcolor: 'action.hover'}}
                            >
                                <ListItemText
                                    primary={notification.message}
                                    secondary={dayjs(notification.createdAt).fromNow()}
                                    slotProps={{primary: {fontWeight: notification.readAt ? 400 : 600}}}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
            </Popover>
        </>
    );
}
